import {Checkbox, ListItemText, TextField} from "@mui/material";
import * as React from "react";
import {useEffect, useState} from "react";
import MenuItem from "@mui/material/MenuItem";
import {useTranslation} from "react-i18next";
import {useRecoilValue, useSetRecoilState} from "recoil";
import * as Yup from "yup";
import AdvancedFilter from "../../../component/AdvancedFilter";
import ColouredDot from "../../../component/ColouredDot";
import {addFilter, getFilterValue} from "../../../utils/FilterUtils";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {
    COLOR_BILLABLE,
    COLOR_BILLED_PAID,
    COLOR_BILLED_UNPAID,
    COLOR_LOADING,
    COLOR_REJECTED, DECIMAL_REG_EXP,
    eTollChargesDefaultValues,
    ID_BILLABLE, ID_BILLED_PAID,
    ID_BILLED_UNPAID,
    ID_FILTER_SEARCH,
    ID_FILTER_TOLL_CUSTOMER_ID,
    ID_FILTER_TOLL_DEALERS,
    ID_FILTER_TOLL_ENTRY_DATE_FROM,
    ID_FILTER_TOLL_ENTRY_DATE_TO,
    ID_FILTER_TOLL_EQUIPMENT_NUMBER,
    ID_FILTER_TOLL_EXIT_NAME,
    ID_FILTER_TOLL_FARE_AMOUNT_FROM,
    ID_FILTER_TOLL_FARE_AMOUNT_TO,
    ID_FILTER_TOLL_LEGAL_ENTITIES,
    ID_FILTER_TOLL_PLATE,
    ID_FILTER_TOLL_RA_CODE,
    ID_FILTER_TOLL_STATUSES,
    ID_FILTER_TOLL_VAT_RATE,
    ID_LOADING,
    ID_REJECTED,
    STATE_ETOLL_CHARGES_ACTIVE_FILTERS
} from "../constants/ETollConstants";
import {DatePicker} from "@mui/x-date-pickers";

function ETollChargesFilter() {

    const {t} = useTranslation('finance', {keyPrefix: 'etoll'});
    const {t: tg} = useTranslation("translation");

    // Use Context
    const {
        globalData: {user},
    } = React.useContext(GlobalContext);

    // Setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));

    const setFilters = useSetRecoilState(STATE_ETOLL_CHARGES_ACTIVE_FILTERS);
    const filterDefs = useRecoilValue(STATE_ETOLL_CHARGES_ACTIVE_FILTERS);
    const [dealers, setDealers] = useState<any[]>(dealerOptions);
    const [search, setSearch] = useState("");

    // Update dealers when dealerOptions changes (e.g., on page reload when user data loads)
    useEffect(() => {
        if (dealerOptions && dealerOptions.length > 0) {
            setDealers(dealerOptions);
        }
    }, [dealerOptions.length]);


    const statuses = [
        {
            value: ID_LOADING,
            label: t('statuses.' + ID_LOADING),
            color: COLOR_LOADING + '.transparent'
        },
        {
            value: ID_REJECTED,
            label: t('statuses.' + ID_REJECTED),
            color: COLOR_REJECTED + '.transparent'
        },
        {
            value: ID_BILLABLE,
            label: t('statuses.' + ID_BILLABLE),
            color: COLOR_BILLABLE + '.transparent'
        },
        {
            value: ID_BILLED_PAID,
            label: t('statuses.' + ID_BILLED_PAID),
            color: COLOR_BILLED_PAID + '.transparent'
        },
        {
            value: ID_BILLED_UNPAID,
            label: t('statuses.' + ID_BILLED_UNPAID),
            color: COLOR_BILLED_UNPAID + '.transparent'
        },
    ];

    const schema = Yup.object().shape({
        equipmentNumber: Yup.string().nullable(),
        plate: Yup.string().nullable(),
        tollDateFrom: Yup.date().nullable().test("testFrom", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.tollDateTo || (value <= context.parent.tollDateTo);
        }).typeError(t('filters.errors.invalid-date')),
        tollDateTo: Yup.date().nullable().test("testTo", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.tollDateFrom || (value >= context.parent.tollDateFrom);
        }).typeError(t('filters.errors.invalid-date')),
        exitTollName: Yup.string().nullable(),
        vatRate: Yup.string().nullable(),
        fareAmountFrom: Yup.string().matches(DECIMAL_REG_EXP, {
            excludeEmptyString: true,
            message: t('import.error-dialog.errors.onlyDecimalNumbers')
        }).test("testFareAmountFrom", t('import.error-dialog.errors.amountCheck'), (value, context) => {
            return (!value || !context.parent.fareAmountTo || value <= context.parent.fareAmountTo)
        }),
        fareAmountTo: Yup.string().matches(DECIMAL_REG_EXP, {
            excludeEmptyString: true,
            message: t('import.error-dialog.errors.onlyDecimalNumbers')
        }).test("testFareAmountTo", t('import.error-dialog.errors.amountCheck'), (value, context) => {
            return (!value || !context.parent.fareAmountFrom || value >= context.parent.fareAmountFrom)
        }),
        customerId: Yup.string().nullable(),
        raCode: Yup.string().nullable(),
        selectedStatuses: Yup.array(),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tg('validation.selectAtLeastOne'))
    });

    const defaultValues = {
        equipmentNumber: getFilterValue(ID_FILTER_TOLL_EQUIPMENT_NUMBER, filterDefs) || "",
        plate: getFilterValue(ID_FILTER_TOLL_PLATE, filterDefs) || "",
        tollDateFrom: getFilterValue(ID_FILTER_TOLL_ENTRY_DATE_FROM, filterDefs) || null,
        tollDateTo: getFilterValue(ID_FILTER_TOLL_ENTRY_DATE_TO, filterDefs) || null,
        exitTollName: getFilterValue(ID_FILTER_TOLL_EXIT_NAME, filterDefs) || "",
        vatRate: getFilterValue(ID_FILTER_TOLL_VAT_RATE, filterDefs) || "",
        fareAmountFrom: getFilterValue(ID_FILTER_TOLL_FARE_AMOUNT_FROM, filterDefs) || "",
        fareAmountTo: getFilterValue(ID_FILTER_TOLL_FARE_AMOUNT_TO, filterDefs) || "",
        customerId: getFilterValue(ID_FILTER_TOLL_CUSTOMER_ID, filterDefs) || "",
        raCode: getFilterValue(ID_FILTER_TOLL_RA_CODE, filterDefs) || "",
        selectedStatuses: getFilterValue(ID_FILTER_TOLL_STATUSES, filterDefs) || [],
        legalEntities:
            getFilterValue(ID_FILTER_TOLL_LEGAL_ENTITIES, filterDefs) || legalEntityOptions.map((x: any) => x.value),
        dealers: getFilterValue(ID_FILTER_TOLL_DEALERS, filterDefs) || [],
    };


    const renderEquipmentNumber = (field: any) => (
        <TextField
            margin="dense"
            id={"equipmentNumber"}
            value={field.value}
            onChange={field.onChange}
            label={t('filters.charge.' + ID_FILTER_TOLL_EQUIPMENT_NUMBER)}
            fullWidth
            variant="standard"
        />
    );

    const renderPlate = (field: any) => (
        <TextField
            margin="dense"
            id={"plate"}
            value={field.value}
            onChange={field.onChange}
            label={t('filters.charge.' + ID_FILTER_TOLL_PLATE)}
            fullWidth
            variant="standard"
        />
    );

    const renderEntryDateFrom = (field: any, error: any, state: any, values: any) => (
        <DatePicker label={t('filters.charge.' + ID_FILTER_TOLL_ENTRY_DATE_FROM)}
                    views={['day']}
                    maxDate={values['tollDateTo'] !== null ? values['tollDateTo'] : null}
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth
                                                        id={"tollDateFrom"}
                                                        error={error !== undefined}
                                                        helperText={error ? error.message : ""}
                    />}
                    inputFormat={"L"}
        />
    )
    const renderEntryDateTo = (field: any, error: any, state: any, values: any) => (
        <DatePicker label={t('filters.charge.' + ID_FILTER_TOLL_ENTRY_DATE_TO)}
                    views={['day']}
                    minDate={values['tollDateFrom'] !== null ? values['tollDateFrom'] : null}
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth
                                                        id={"tollDateTo"}
                                                        error={error !== undefined}
                                                        helperText={error ? error.message : ""}
                    />}
                    inputFormat={"L"}
        />
    )

    const renderExitName = (field: any) => (
        <TextField
            margin="dense"
            id="exitTollName"
            value={field.value}
            onChange={field.onChange}
            label={t('filters.charge.' + ID_FILTER_TOLL_EXIT_NAME)}
            fullWidth
            variant="standard"
        />
    );

    const renderVatRate = (field: any) => (
        <TextField
            margin="dense"
            id="vatRate"
            label={t('filters.charge.' + ID_FILTER_TOLL_VAT_RATE)}
            fullWidth
            variant="standard"
            value={field.value}
            onChange={field.onChange}
        />
    );


    const renderAmountFrom = (field: any, error: any) => (
        <TextField
            margin="dense"
            id="fareAmountFrom"
            label={t('filters.charge.' + ID_FILTER_TOLL_FARE_AMOUNT_FROM)}
            fullWidth
            variant="standard"
            value={field.value}
            onChange={field.onChange}
            error={error !== undefined}
            helperText={error ? error.message : ""}
        />
    );

    const renderAmountTo = (field: any, error: any) => (
        <TextField
            margin="dense"
            id="fareAmountTo"
            label={t('filters.charge.' + ID_FILTER_TOLL_FARE_AMOUNT_TO)}
            fullWidth
            variant="standard"
            value={field.value}
            onChange={field.onChange}
            error={error !== undefined}
            helperText={error ? error.message : ""}
        />
    );

    const renderCustomerId = (field: any) => (
        <TextField
            margin="dense"
            id="customerId"
            label={t('filters.charge.' + ID_FILTER_TOLL_CUSTOMER_ID)}
            fullWidth
            variant="standard"
            value={field.value}
            onChange={field.onChange}
        />
    );


    const renderRaCode = (field: any) => (
        <TextField
            margin="dense"
            id="raCode"
            label={t('filters.charge.' + ID_FILTER_TOLL_RA_CODE)}
            fullWidth
            variant="standard"
            value={field.value}
            onChange={field.onChange}
        />
    );

    const renderStatuses = (field: any) => (
        <TextField fullWidth
                   label={t('filters.charge.' + ID_FILTER_TOLL_STATUSES)}
                   select
                   SelectProps={{
                       multiple: true,
                       renderValue: (selected: any) => selected.map((s: any) => statuses.find(ss => ss.value === s)!.label).join(', ')
                   }}
                   value={field.value}
                   onChange={field.onChange}
        >
            {statuses.map((status) =>
                <MenuItem key={status.value} value={status.value}>
                    <Checkbox checked={field.value.indexOf(status.value) > -1}/>
                    <ColouredDot color={status?.color}/>
                    <ListItemText primary={status.label}/>
                </MenuItem>
            )}
        </TextField>
    )

    const renderMultiSelect = (label: string, list: any[], name: string, isHidden: boolean, onChange?: any) => {
        return (field: any, error: any, formState: any, watchAll: any, setValue: any) => (
            <TextField
                name={name}
                fullWidth
                select
                hidden={isHidden}
                disabled={list.length == 0}
                SelectProps={{
                    multiple: true,
                    renderValue: (selected: any) =>
                        selected
                            .map((s: any) => list.find((ss) => ss.value === s)!.label)
                            .join(", "),
                }}
                label={label}
                value={field.value}
                onChange={(event) => {
                    if (onChange) {
                        onChange(event.target.value, setValue);
                    }
                    field.onChange(event);
                }}
                error={error !== undefined}
                helperText={error ? error.message : ""}
            >
                {list.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                        <Checkbox checked={field.value?.indexOf(item.value) > -1}/>
                        <ListItemText primary={item.label}/>
                    </MenuItem>
                ))}
            </TextField>
        );
    };

    const fields = {
        equipmentNumber: renderEquipmentNumber,
        plate: renderPlate,
        tollDateFrom: renderEntryDateFrom,
        tollDateTo: renderEntryDateTo,
        exitTollName: renderExitName,
        vatRate: renderVatRate,
        fareAmountFrom: renderAmountFrom,
        fareAmountTo: renderAmountTo,
        customerId: renderCustomerId,
        raCode: renderRaCode,
        selectedStatuses: renderStatuses,
        legalEntities: renderMultiSelect(
            tg("shared.legalEntities"),
            legalEntityOptions,
            "legalEntities",
            legalEntityOptions.length <= 1,
            (value: any, setValue: any) => {
                setValue('dealers', []);
                let dealers = user?.dealers
                    ?.filter(
                        (x: any) =>
                            value?.length > 0 &&
                            x.legalEntities.some((e: any) => value.includes(e.name)) &&
                            x.name.toLowerCase() !== "all"
                    )
                    .map((x: any) => {
                        return {
                            label: tg(`dealers.${x.name}`),
                            value: x.name,
                        };
                    });
                setDealers(dealers);
            }
        ),
        dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1)
    }


    const submit = (data: any) => {
        addFilter(setFilters, ID_FILTER_SEARCH, search);
        addFilter(setFilters, ID_FILTER_TOLL_EQUIPMENT_NUMBER, data.equipmentNumber);
        addFilter(setFilters, ID_FILTER_TOLL_PLATE, data.tollPlate);
        addFilter(setFilters, ID_FILTER_TOLL_ENTRY_DATE_FROM, data.tollDateFrom);
        addFilter(setFilters, ID_FILTER_TOLL_ENTRY_DATE_TO, data.tollDateTo);
        addFilter(setFilters, ID_FILTER_TOLL_EXIT_NAME, data.exitTollName);
        addFilter(setFilters, ID_FILTER_TOLL_VAT_RATE, data.vatRate);
        addFilter(setFilters, ID_FILTER_TOLL_FARE_AMOUNT_FROM, data.fareAmountFrom);
        addFilter(setFilters, ID_FILTER_TOLL_FARE_AMOUNT_TO, data.fareAmountTo);
        addFilter(setFilters, ID_FILTER_TOLL_CUSTOMER_ID, data.customerId);
        addFilter(setFilters, ID_FILTER_TOLL_RA_CODE, data.raCode);
        addFilter(setFilters, ID_FILTER_TOLL_STATUSES, data.selectedStatuses);
        addFilter(setFilters, ID_FILTER_TOLL_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilters, ID_FILTER_TOLL_DEALERS, data.dealers);
    }

    const reset = () => {
        setFilters([]);
        setDealers(dealerOptions);
    }

    return (
        <AdvancedFilter schema={schema} fields={fields} defaultValues={defaultValues}
                        searchInput={search} setSearchInput={setSearch}
                        onSubmit={submit}
                        onReset={reset}
                        customDefaultValues={eTollChargesDefaultValues}
        />
    )
}

export default ETollChargesFilter;